import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Logger } from '../logger';
import { Observable } from 'rxjs';
declare const google: any;

// How far a unit's point may sit from the picked street address; a gated complex fits inside it.
const MAX_UNIT_DISTANCE_METERS = 300;
// A unit Google does not know comes back on the point it has for the street address itself,
// so the address without the unit is looked up too and an answer this close to it is not trusted.
const SAME_POINT_METERS = 5;

export type GeocodeLatLng = { latitude: number | string; longitude: number | string };

function distanceInMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(a));
}

@Injectable()
export class GoogleGeocode {

  private mapsBaseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
  private mapsBaseJsonUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
  private url = environment.google_geocode_api;
  private tidyKey = environment.google_geocode_key;
  googleGeocoder;

  constructor(
    private http: HttpClient,
    private logger: Logger
  ) {
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
          reject(new Error('Google Maps API failed to load'));
        }
      }, 10000);
    });
  }

  private async getGeocoder(): Promise<any> {
    await this.ensureGoogleMapsLoaded();
    if (!this.googleGeocoder) {
      this.googleGeocoder = new google.maps.Geocoder();
    }
    return this.googleGeocoder;
  }

  async getGeocode(placeId) {
    try {
      const geocoder = await this.getGeocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ placeId }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve({
              results,
              status
            });
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getLatLong(placeId) {
    try {
      const geocoder = await this.getGeocoder();
      return await new Promise((resolve, reject) => {
        geocoder.geocode({placeId}, (geocoderResult) => {
          if (geocoderResult === null) {
            reject({
              latitude: '',
              longitude: ''
            })
          } else {
            resolve({
              latitude: geocoderResult[0]?.geometry?.location?.lat(),
              longitude: geocoderResult[0]?.geometry?.location?.lng()
            });
          }
        });
      });
    } catch (err) {
      this.logger.error(err, 'error getting latitude and longitude');

      return { latitude: '', longitude: '' };
    }
  }

  private async geocodeAddress(address: string): Promise<any | null> {
    try {
      const geocoder = await this.getGeocoder();
      return await new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          resolve(status === 'OK' && results?.length ? results[0] : null);
        });
      });
    } catch (err) {
      this.logger.error(err, 'error geocoding address');
      return null;
    }
  }

  private buildAddressWithUnit(
    parts: { address1?: string; address2?: string; city?: string; state?: string; zip?: string },
    unit: string
  ): string {
    const stateZip = [parts.state, parts.zip].map(part => (part || '').toString().trim()).filter(Boolean).join(' ');
    return [`${(parts.address1 || '').toString().trim()} ${unit}`.trim(), (parts.city || '').toString().trim(), stateZip]
      .filter(Boolean)
      .join(', ');
  }

  // Returns coordinates that account for the unit (address2). Geocoding by the base
  // street address alone lands multi-unit buildings on a street-centroid point (wrong
  // building on the map). When a unit is present we geocode the address with it and
  // without it. Google's unit answer is taken only when it is a full match on a point of
  // its own, near the picked place: for a unit it does not know, Google echoes the unit and
  // returns the street address's own point, and sometimes it drops the unit and matches
  // another house on the same street, kilometres away. A made-up unit is no test, since
  // Google answers those inconsistently. Otherwise we keep the caller's coords.
  async getLatLongWithUnit(
    parts: { address1?: string; address2?: string; city?: string; state?: string; zip?: string },
    fallback: GeocodeLatLng
  ): Promise<GeocodeLatLng> {
    const unit = (parts?.address2 || '').toString().trim();
    if (!unit) {
      return fallback;
    }
    const [result, base] = await Promise.all([
      this.geocodeAddress(this.buildAddressWithUnit(parts, unit)),
      this.geocodeAddress(this.buildAddressWithUnit(parts, ''))
    ]);
    if (!result || result.partial_match || !base || base.partial_match) {
      return fallback;
    }
    const latitude = result.geometry?.location?.lat();
    const longitude = result.geometry?.location?.lng();
    const baseLat = base.geometry?.location?.lat();
    const baseLng = base.geometry?.location?.lng();
    if (![latitude, longitude, baseLat, baseLng].every(Number.isFinite)
      || distanceInMeters(latitude, longitude, baseLat, baseLng) <= SAME_POINT_METERS) {
      return fallback;
    }
    const fallbackLat = Number(fallback?.latitude);
    const fallbackLng = Number(fallback?.longitude);
    const hasFallback = fallback?.latitude !== '' && fallback?.longitude !== ''
      && Number.isFinite(fallbackLat) && Number.isFinite(fallbackLng);
    const distance = hasFallback
      ? distanceInMeters(latitude, longitude, fallbackLat, fallbackLng)
      : distanceInMeters(latitude, longitude, baseLat, baseLng);
    if (distance > MAX_UNIT_DISTANCE_METERS) {
      return fallback;
    }
    return { latitude, longitude };
  }

  getAddressImage(address): Observable<Blob> {
    return this.http.get(`${this.mapsBaseUrl}?center=${address.address1}+,${address.add_state}+${address.zip}&zoom=20&size=640x640&scale=2&maptype=satellite${this.tidyKey}`, { responseType: 'blob' });
  }

  getLocationByCoordinates(latitude: string, longitude: string): Promise<any> {
    return this.http.get(`${this.mapsBaseJsonUrl}?latlng=${latitude},${longitude}${this.tidyKey}`)
    .toPromise()
  }
}
